import { COMBO_MAX_LENGTH, HIEROGLYPH_COUNT } from './constants';

// ============================================================
// PORTAL ADDRESS
// Represents a destination for portal teleportation
// ============================================================

export interface PortalAddress {
    // World identifier (could be URL, worldId, or special value)
    worldId: string;
    // Spawn coordinates in destination world (optional)
    spawnX?: number;
    spawnY?: number;
    // Is this a remote server or local/embedded?
    isRemote: boolean;
    // Server URL for remote worlds
    serverUrl?: string;
}

// ============================================================
// PORTAL REGISTRY
// Maps portal codes to destinations
// ============================================================

export class PortalRegistry {
    // Map from portal code string to destination
    // Code string format: "h1,h2,h3" (hieroglyph indices separated by commas)
    private registry: Map<string, PortalAddress> = new Map();

    // Default portal destination (when code is empty or "0")
    // Hieroglyph 0 portal teleports to the shared Fly.io server
    private defaultDestination: PortalAddress = {
        worldId: 'shared',
        isRemote: true,
        serverUrl: 'wss://blockworld-server-v2.fly.dev',
        spawnX: 0,  // Spawn at world center (portal location)
    };

    constructor() {
        // Register the default portal (single rune with hieroglyph 0)
        this.register([0], this.defaultDestination);
    }

    /**
     * Convert a portal code array to a string key
     */
    private codeToKey(code: number[]): string {
        // Normalize: remove trailing zeros, limit to max length
        const normalized = code.slice(0, COMBO_MAX_LENGTH);
        while (normalized.length > 1 && normalized[normalized.length - 1] === 0) {
            normalized.pop();
        }
        return normalized.join(',');
    }

    /**
     * Register a portal code to a destination
     */
    register(code: number[], destination: PortalAddress): void {
        const key = this.codeToKey(code);
        this.registry.set(key, destination);
    }

    /**
     * Unregister a portal code
     */
    unregister(code: number[]): boolean {
        const key = this.codeToKey(code);
        return this.registry.delete(key);
    }

    /**
     * Look up a destination for a portal code
     * Returns undefined if no destination is registered
     */
    lookup(code: number[]): PortalAddress | undefined {
        const key = this.codeToKey(code);
        return this.registry.get(key);
    }

    /**
     * Check if a portal code is registered
     */
    isRegistered(code: number[]): boolean {
        const key = this.codeToKey(code);
        return this.registry.has(key);
    }

    /**
     * Get the default destination
     */
    getDefault(): PortalAddress {
        return this.defaultDestination;
    }

    /**
     * Set the default destination
     */
    setDefault(destination: PortalAddress): void {
        this.defaultDestination = destination;
        // Also update the [0] registration
        this.register([0], destination);
    }

    /**
     * Get all registered portal codes
     */
    getAllCodes(): number[][] {
        const codes: number[][] = [];
        for (const key of this.registry.keys()) {
            codes.push(key.split(',').map(Number));
        }
        return codes;
    }

    /**
     * Export registry to JSON for persistence
     */
    toJSON(): { code: number[]; destination: PortalAddress }[] {
        const entries: { code: number[]; destination: PortalAddress }[] = [];
        for (const [key, destination] of this.registry) {
            entries.push({
                code: key.split(',').map(Number),
                destination,
            });
        }
        return entries;
    }

    /**
     * Import registry from JSON
     */
    fromJSON(entries: { code: number[]; destination: PortalAddress }[]): void {
        this.registry.clear();
        for (const entry of entries) {
            this.register(entry.code, entry.destination);
        }
    }

    /**
     * Generate a unique portal code that isn't registered
     * Useful for creating new portal connections
     */
    generateUniqueCode(): number[] {
        // Try single hieroglyphs first
        for (let h = 1; h < HIEROGLYPH_COUNT; h++) {
            if (!this.isRegistered([h])) {
                return [h];
            }
        }

        // Try double combinations
        for (let h1 = 1; h1 < HIEROGLYPH_COUNT; h1++) {
            for (let h2 = 1; h2 < HIEROGLYPH_COUNT; h2++) {
                if (!this.isRegistered([h1, h2])) {
                    return [h1, h2];
                }
            }
        }

        // Try triple combinations
        for (let h1 = 1; h1 < HIEROGLYPH_COUNT; h1++) {
            for (let h2 = 1; h2 < HIEROGLYPH_COUNT; h2++) {
                for (let h3 = 1; h3 < HIEROGLYPH_COUNT; h3++) {
                    if (!this.isRegistered([h1, h2, h3])) {
                        return [h1, h2, h3];
                    }
                }
            }
        }

        // All codes taken (very unlikely with 100^3 possibilities)
        throw new Error('No unique portal codes available');
    }
}

// ============================================================
// GLOBAL REGISTRY INSTANCE
// Shared between client and server
// ============================================================

export const globalPortalRegistry = new PortalRegistry();
